import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Mail,
  Plus,
  Save,
  Trash2,
  Send,
  Route,
  FileText,
  RefreshCw,
  Pencil,
  Eye,
} from 'lucide-react';
import { EmailTemplatePreview } from '../components/email/EmailTemplatePreview';
import { Layout } from '../components/layout/Layout';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Sheet } from '../components/ui/Sheet';
import { Badge } from '../components/ui/Badge';
import { useToast } from '../components/ui/Toast';
import { api } from '../services/api';
import type { EmailEvent, EmailRule, EmailTemplate } from '../types';
import { cn } from '../utils/helpers';
import {
  buildPreviewVariables,
  parsePreviewVariablesText,
  previewVariablesToText,
} from '../utils/emailPreview';
import { BUILTIN_EMAIL_TRIGGER_COUNT } from '../constants/emailCatalog';
import {
  assignmentsToRules,
  countUncoveredTriggers,
  rulesToAssignments,
  triggerSupportsUserType,
  type TriggerAssignment,
} from '../utils/emailRouting';

type Tab = 'templates' | 'routing';

type TemplateForm = {
  id: string;
  name: string;
  subject: string;
  html_body: string;
  description: string;
  variables: string;
};

const emptyTemplateForm: TemplateForm = {
  id: '',
  name: '',
  subject: '',
  html_body: '',
  description: '',
  variables: '',
};

function parseVariables(raw: string): string[] {
  return raw
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
}

function variablesToString(variables: string[]): string {
  return variables.join(', ');
}

export function Email() {
  const { toast } = useToast();
  const [tab, setTab] = useState<Tab>('templates');
  const [loading, setLoading] = useState(true);
  const [savingRules, setSavingRules] = useState(false);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [rules, setRules] = useState<EmailRule[]>([]);
  const [events, setEvents] = useState<EmailEvent[]>([]);
  const [assignments, setAssignments] = useState<TriggerAssignment[]>([]);
  const [rulesDirty, setRulesDirty] = useState(false);

  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [templateForm, setTemplateForm] = useState<TemplateForm>(emptyTemplateForm);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testTemplateId, setTestTemplateId] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [testVariables, setTestVariables] = useState('otp=123456\nexpiry_minutes=5');
  const [sendingTest, setSendingTest] = useState(false);

  const [selectedPreviewId, setSelectedPreviewId] = useState<string | null>(null);
  const [previewVariablesText, setPreviewVariablesText] = useState('');

  const [seedingRules, setSeedingRules] = useState(false);

  const applyRulesPayload = useCallback(
    (rulesPayload: EmailRule[], eventsPayload: EmailEvent[], templatesPayload: EmailTemplate[]) => {
      const templateIds = templatesPayload.map((t) => t.id);
      setTemplates(templatesPayload);
      setRules(rulesPayload);
      setEvents(eventsPayload);
      setAssignments(rulesToAssignments(rulesPayload, eventsPayload, templateIds));
      setRulesDirty(false);
    },
    []
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      let rulesRes = await api.getEmailRules();
      const incomplete =
        rulesRes.events.length < BUILTIN_EMAIL_TRIGGER_COUNT ||
        rulesRes.rules.length < BUILTIN_EMAIL_TRIGGER_COUNT ||
        rulesRes.templates.length < BUILTIN_EMAIL_TRIGGER_COUNT;
      if (incomplete) {
        rulesRes = await api.getEmailRules({ reset: true });
        toast('Loaded all 6 email templates and routing rules', 'success');
      }
      applyRulesPayload(rulesRes.rules, rulesRes.events, rulesRes.templates);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to load email settings', 'error');
    } finally {
      setLoading(false);
    }
  }, [applyRulesPayload, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const templateMap = useMemo(
    () => Object.fromEntries(templates.map((t) => [t.id, t])),
    [templates]
  );

  const eventMap = useMemo(() => Object.fromEntries(events.map((e) => [e.id, e])), [events]);

  const syncPreviewVariablesText = (variableNames: string[]) => {
    setPreviewVariablesText(previewVariablesToText(buildPreviewVariables(variableNames)));
  };

  const openCreateTemplate = () => {
    setEditingTemplateId(null);
    setTemplateForm(emptyTemplateForm);
    syncPreviewVariablesText([]);
    setTemplateModalOpen(true);
  };

  const openEditTemplate = (template: EmailTemplate) => {
    setEditingTemplateId(template.id);
    setTemplateForm({
      id: template.id,
      name: template.name,
      subject: template.subject,
      html_body: template.html_body,
      description: template.description,
      variables: variablesToString(template.variables),
    });
    syncPreviewVariablesText(template.variables);
    setTemplateModalOpen(true);
  };

  const openListPreview = (template: EmailTemplate) => {
    setSelectedPreviewId(template.id);
    setPreviewVariablesText(previewVariablesToText(buildPreviewVariables(template.variables)));
  };

  const closePreviewSheet = () => setSelectedPreviewId(null);

  const selectedPreviewTemplate = selectedPreviewId ? templateMap[selectedPreviewId] : null;

  const modalPreviewVariables = useMemo(
    () => parsePreviewVariablesText(previewVariablesText),
    [previewVariablesText]
  );

  const modalVariableNames = useMemo(
    () => parseVariables(templateForm.variables),
    [templateForm.variables]
  );

  const saveTemplate = async () => {
    setSavingTemplate(true);
    try {
      const payload = {
        id: templateForm.id.trim() || undefined,
        name: templateForm.name.trim(),
        subject: templateForm.subject.trim(),
        html_body: templateForm.html_body,
        description: templateForm.description.trim(),
        variables: parseVariables(templateForm.variables),
      };
      if (!payload.name || !payload.subject || !payload.html_body) {
        toast('Name, subject, and HTML body are required', 'error');
        return;
      }
      if (editingTemplateId) {
        await api.updateEmailTemplate(editingTemplateId, payload);
        toast('Template updated', 'success');
      } else {
        await api.createEmailTemplate({
          id: payload.id || '',
          name: payload.name,
          subject: payload.subject,
          html_body: payload.html_body,
          description: payload.description,
          variables: payload.variables,
        });
        toast('Template created', 'success');
      }
      setTemplateModalOpen(false);
      await loadData();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to save template', 'error');
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async (templateId: string) => {
    if (!confirm('Delete this template?')) return;
    try {
      await api.deleteEmailTemplate(templateId);
      if (selectedPreviewId === templateId) setSelectedPreviewId(null);
      toast('Template deleted', 'success');
      await loadData();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to delete template', 'error');
    }
  };

  const updateAssignment = (trigger: string, patch: Partial<TriggerAssignment>) => {
    setAssignments((prev) =>
      prev.map((row) => (row.trigger === trigger ? { ...row, ...patch } : row))
    );
    setRulesDirty(true);
  };

  const restoreDefaultRules = async () => {
    setSeedingRules(true);
    try {
      const rulesRes = await api.getEmailRules({ reset: true });
      applyRulesPayload(rulesRes.rules, rulesRes.events, rulesRes.templates);
      toast('Default routing restored', 'success');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to restore default rules', 'error');
    } finally {
      setSeedingRules(false);
    }
  };

  const saveRules = async () => {
    const nextRules = assignmentsToRules(assignments);
    const missingTemplate = nextRules.find(
      (rule) => rule.enabled && !templateMap[rule.template_id]
    );
    if (missingTemplate) {
      toast('Each enabled trigger needs a valid template', 'error');
      return;
    }

    setSavingRules(true);
    try {
      const res = await api.updateEmailRules(nextRules);
      applyRulesPayload(res.rules, events, templates);
      toast('Routing saved', 'success');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to save routing', 'error');
    } finally {
      setSavingRules(false);
    }
  };

  const uncoveredCount = countUncoveredTriggers(assignments);

  const openTestSend = (templateId: string) => {
    const template = templateMap[templateId];
    setTestTemplateId(templateId);
    setTestEmail('');
    setTestVariables(
      previewVariablesToText(buildPreviewVariables(template?.variables ?? []))
    );
    setTestModalOpen(true);
  };

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const variables: Record<string, string | number> = {};
      testVariables.split('\n').forEach((line) => {
        const [key, ...rest] = line.split('=');
        if (!key?.trim()) return;
        const value = rest.join('=').trim();
        const num = Number(value);
        variables[key.trim()] = Number.isFinite(num) && value !== '' ? num : value;
      });
      await api.sendTestEmail({
        template_id: testTemplateId,
        to_email: testEmail.trim(),
        variables,
      });
      toast('Test email sent', 'success');
      setTestModalOpen(false);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to send test email', 'error');
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-[#f8fafc] flex items-center gap-2">
              <Mail className="w-6 h-6 text-[#3b82f6]" />
              Email
            </h1>
            <p className="mt-1 text-sm text-[#94a3b8]">
              Manage HTML templates and which template sends on each trigger.
            </p>
          </div>
          <Button variant="secondary" icon={<RefreshCw className="w-4 h-4" />} onClick={loadData} disabled={loading}>
            Refresh
          </Button>
        </div>

        <div className="flex gap-2 border-b border-[#252535] pb-1">
          {[
            { id: 'templates' as Tab, label: 'Templates', icon: FileText },
            { id: 'routing' as Tab, label: 'Routing', icon: Route },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors',
                tab === id
                  ? 'text-[#3b82f6] border-b-2 border-[#3b82f6] -mb-[2px]'
                  : 'text-[#94a3b8] hover:text-[#f1f5f9]'
              )}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-[#3b82f6] border-t-transparent rounded-full" />
          </div>
        ) : tab === 'templates' ? (
          <Card padding="none">
              <div className="p-6 pb-0">
                <CardHeader
                  title="Email templates"
                  subtitle="HTML bodies use Jinja2 variables, e.g. {{ otp }}"
                  action={
                    <Button icon={<Plus className="w-4 h-4" />} onClick={openCreateTemplate}>
                      New template
                    </Button>
                  }
                />
              </div>
              <div className="space-y-3 px-6 pb-6">
              {templates.length === 0 ? (
                <p className="text-sm text-[#64748b] py-8 text-center">No templates yet.</p>
              ) : (
                templates.map((template) => (
                  <div
                    key={template.id}
                    className="flex flex-col gap-3 rounded-xl border border-[#252535] bg-[#10101a] p-4 transition-colors hover:border-[#353548] sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold text-[#f8fafc]">{template.name}</p>
                        <Badge variant="info">{template.id}</Badge>
                      </div>
                      <p className="mt-1 text-sm text-[#94a3b8] truncate">{template.subject}</p>
                      {template.variables.length > 0 && (
                        <p className="mt-2 text-xs text-[#64748b]">
                          Variables: {template.variables.map((v) => `{{ ${v} }}`).join(', ')}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={<Eye className="w-3.5 h-3.5" />}
                        onClick={() => openListPreview(template)}
                      >
                        Preview
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={<Send className="w-3.5 h-3.5" />}
                        onClick={() => openTestSend(template.id)}
                      >
                        Test
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={<Pencil className="w-3.5 h-3.5" />}
                        onClick={() => openEditTemplate(template)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        icon={<Trash2 className="w-3.5 h-3.5" />}
                        onClick={() => deleteTemplate(template.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))
              )}
              </div>
          </Card>
        ) : (
          <Card>
            <CardHeader
              title="Email routing"
              subtitle="Each app trigger sends one template. Edit inline, then save."
              action={
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    icon={<RefreshCw className="w-4 h-4" />}
                    onClick={restoreDefaultRules}
                    loading={seedingRules}
                  >
                    Restore defaults
                  </Button>
                  <Button
                    icon={<Save className="w-4 h-4" />}
                    onClick={saveRules}
                    loading={savingRules}
                    disabled={!rulesDirty}
                  >
                    Save routing
                  </Button>
                </div>
              }
            />
            <div className="space-y-4 px-6 pb-6">
              {rulesDirty && (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
                  Unsaved changes - click <strong>Save routing</strong> to persist.
                </div>
              )}

              {templates.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[#364152] py-12 text-center">
                  <p className="text-sm text-[#94a3b8] mb-4">
                    Create email templates first, then map them to triggers here.
                  </p>
                  <Button variant="secondary" onClick={() => setTab('templates')}>
                    Go to templates
                  </Button>
                </div>
              ) : (
                <>
                  {uncoveredCount > 0 && (
                    <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                      {uncoveredCount} enabled trigger{uncoveredCount === 1 ? '' : 's'} missing a template.
                    </div>
                  )}

                  {rules.length > assignments.length && (
                    <div className="rounded-xl border border-[#364152] bg-[#0f0f1a] px-4 py-3 text-sm text-[#94a3b8]">
                      {rules.length - assignments.length} extra rule
                      {rules.length - assignments.length === 1 ? '' : 's'} in storage will be replaced on save
                      (one rule per trigger).
                    </div>
                  )}

                  <div className="overflow-x-auto rounded-xl border border-[#252535]">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-[#0f0f1a] text-[#64748b] text-xs uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-3 font-semibold min-w-[200px]">When (trigger)</th>
                        <th className="px-4 py-3 font-semibold min-w-[220px]">Template</th>
                        <th className="px-4 py-3 font-semibold min-w-[140px]">Audience</th>
                        <th className="px-4 py-3 font-semibold w-24">Send</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#252535]">
                      {assignments.map((row) => {
                        const event = eventMap[row.trigger];
                        const selectedTemplate = templateMap[row.templateId];
                        return (
                            <tr key={row.trigger} className="bg-[#10101a] hover:bg-[#151523] align-top">
                              <td className="px-4 py-3">
                                <p className="font-medium text-[#f1f5f9]">{event?.label || row.trigger}</p>
                                <p className="text-xs text-[#64748b] mt-1 leading-relaxed">
                                  {event?.description}
                                </p>
                                <p className="text-xs font-mono text-[#475569] mt-1">{row.trigger}</p>
                              </td>
                              <td className="px-4 py-3">
                                <select
                                  value={row.templateId}
                                  onChange={(e) =>
                                    updateAssignment(row.trigger, { templateId: e.target.value })
                                  }
                                  disabled={!row.enabled}
                                  className="w-full min-w-[180px] rounded-lg border border-[#252535] bg-[#0f0f1a] px-3 py-2 text-sm text-[#f1f5f9] disabled:opacity-50"
                                >
                                  <option value="">Select template…</option>
                                  {templates.map((template) => (
                                    <option key={template.id} value={template.id}>
                                      {template.name} ({template.id})
                                    </option>
                                  ))}
                                </select>
                                {selectedTemplate && (
                                  <p className="mt-1 text-xs text-[#64748b] truncate">
                                    Subject: {selectedTemplate.subject}
                                  </p>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                {triggerSupportsUserType(row.trigger) ? (
                                  <select
                                    value={row.userType}
                                    onChange={(e) =>
                                      updateAssignment(row.trigger, { userType: e.target.value })
                                    }
                                    disabled={!row.enabled}
                                    className="w-full rounded-lg border border-[#252535] bg-[#0f0f1a] px-3 py-2 text-sm text-[#f1f5f9] disabled:opacity-50"
                                  >
                                    <option value="">Any user type</option>
                                    <option value="company">Company only</option>
                                    <option value="job_seeker">Job seeker only</option>
                                  </select>
                                ) : (
                                  <span className="text-[#64748b]">All users</span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <label className="flex cursor-pointer items-center gap-2 text-sm text-[#94a3b8]">
                                  <input
                                    type="checkbox"
                                    checked={row.enabled}
                                    onChange={(e) =>
                                      updateAssignment(row.trigger, { enabled: e.target.checked })
                                    }
                                    className="rounded border-[#252535]"
                                  />
                                  {row.enabled ? 'On' : 'Off'}
                                </label>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                  </div>

                  <p className="text-xs text-[#64748b]">
                    {assignments.filter((a) => a.enabled).length} of {assignments.length} triggers active
                    · {templates.length} of {BUILTIN_EMAIL_TRIGGER_COUNT} templates in catalog
                  </p>
                </>
              )}
            </div>
          </Card>
        )}
      </div>

      <Sheet
        isOpen={!!selectedPreviewTemplate}
        onClose={closePreviewSheet}
        title="Template preview"
        subtitle={
          selectedPreviewTemplate
            ? `${selectedPreviewTemplate.name} · ${selectedPreviewTemplate.id}`
            : undefined
        }
        size="xl"
        footer={
          selectedPreviewTemplate ? (
            <>
              <Button
                variant="secondary"
                icon={<Pencil className="w-4 h-4" />}
                onClick={() => {
                  closePreviewSheet();
                  openEditTemplate(selectedPreviewTemplate);
                }}
              >
                Edit
              </Button>
              <Button
                icon={<Send className="w-4 h-4" />}
                onClick={() => {
                  closePreviewSheet();
                  openTestSend(selectedPreviewTemplate.id);
                }}
              >
                Test send
              </Button>
            </>
          ) : undefined
        }
      >
        {selectedPreviewTemplate && (
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#94a3b8]">
                Preview variables (key=value)
              </label>
              <textarea
                value={previewVariablesText}
                onChange={(e) => setPreviewVariablesText(e.target.value)}
                rows={4}
                className="w-full rounded-xl border border-[#252535] bg-[#0f0f1a] px-3 py-2.5 font-mono text-sm text-[#f1f5f9]"
              />
            </div>
            <EmailTemplatePreview
              subject={selectedPreviewTemplate.subject}
              htmlBody={selectedPreviewTemplate.html_body}
              variableNames={selectedPreviewTemplate.variables}
              previewVariables={parsePreviewVariablesText(previewVariablesText)}
              tall
            />
          </div>
        )}
      </Sheet>

      <Modal
        isOpen={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        title={editingTemplateId ? 'Edit template' : 'New template'}
        size="xl"
        allowDismiss={!savingTemplate}
        footer={
          <>
            <Button variant="secondary" onClick={() => setTemplateModalOpen(false)} disabled={savingTemplate}>
              Cancel
            </Button>
            <Button onClick={saveTemplate} loading={savingTemplate}>
              Save
            </Button>
          </>
        }
      >
        <div className="grid gap-6 lg:grid-cols-2 max-h-[75vh] overflow-hidden">
          <div className="space-y-4 overflow-y-auto pr-1 min-h-0">
            {!editingTemplateId && (
              <Input
                label="Template ID (optional)"
                placeholder="company-otp"
                value={templateForm.id}
                onChange={(e) => setTemplateForm((f) => ({ ...f, id: e.target.value }))}
              />
            )}
            <Input
              label="Name"
              value={templateForm.name}
              onChange={(e) => setTemplateForm((f) => ({ ...f, name: e.target.value }))}
            />
            <Input
              label="Subject"
              placeholder="Supports {{ variables }}"
              value={templateForm.subject}
              onChange={(e) => setTemplateForm((f) => ({ ...f, subject: e.target.value }))}
            />
            <Input
              label="Variables (comma-separated)"
              placeholder="otp, expiry_minutes"
              value={templateForm.variables}
              onChange={(e) => {
                const next = e.target.value;
                setTemplateForm((f) => ({ ...f, variables: next }));
                syncPreviewVariablesText(parseVariables(next));
              }}
            />
            <Input
              label="Description"
              value={templateForm.description}
              onChange={(e) => setTemplateForm((f) => ({ ...f, description: e.target.value }))}
            />
            <div>
              <label className="block text-xs font-semibold text-[#94a3b8] mb-1.5 uppercase tracking-wider">
                HTML body (Jinja2)
              </label>
              <textarea
                value={templateForm.html_body}
                onChange={(e) => setTemplateForm((f) => ({ ...f, html_body: e.target.value }))}
                rows={12}
                className="w-full bg-[#0f0f1a] border border-[#252535] rounded-xl text-[#f1f5f9] text-sm font-mono px-3 py-2.5 resize-y min-h-[180px]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#94a3b8] mb-1.5 uppercase tracking-wider">
                Preview variables (key=value)
              </label>
              <textarea
                value={previewVariablesText}
                onChange={(e) => setPreviewVariablesText(e.target.value)}
                rows={4}
                className="w-full bg-[#0f0f1a] border border-[#252535] rounded-xl text-[#f1f5f9] text-sm font-mono px-3 py-2.5"
              />
            </div>
          </div>

          <div className="overflow-y-auto min-h-0 lg:border-l lg:border-[#2a2a3a] lg:pl-6">
            <EmailTemplatePreview
              subject={templateForm.subject}
              htmlBody={templateForm.html_body}
              variableNames={modalVariableNames}
              previewVariables={modalPreviewVariables}
            />
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={testModalOpen}
        onClose={() => setTestModalOpen(false)}
        title="Send test email"
        allowDismiss={!sendingTest}
        footer={
          <>
            <Button variant="secondary" onClick={() => setTestModalOpen(false)} disabled={sendingTest}>
              Cancel
            </Button>
            <Button onClick={sendTest} loading={sendingTest} icon={<Send className="w-4 h-4" />}>
              Send
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="To email"
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="you@example.com"
          />
          <div>
            <label className="block text-xs font-semibold text-[#94a3b8] mb-1.5 uppercase tracking-wider">
              Variables (one per line: key=value)
            </label>
            <textarea
              value={testVariables}
              onChange={(e) => setTestVariables(e.target.value)}
              rows={5}
              className="w-full bg-[#0f0f1a] border border-[#252535] rounded-xl text-[#f1f5f9] text-sm font-mono px-3 py-2.5"
            />
          </div>
        </div>
      </Modal>
    </Layout>
  );
}
